<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';

import ContactCustomAttributeItem from 'dashboard/components-next/Contacts/ContactsSidebar/ContactCustomAttributeItem.vue';
import {
  findListAttribute,
  STAGE_ATTRIBUTE_HINT,
} from 'dashboard/components-next/Contacts/contactStageColors';
import { isTrackingAttribute } from 'dashboard/components-next/Contacts/contactTracking';
import { syncedListAttributes } from 'dashboard/components-next/Contacts/syncedAttributes';

// "Atributos" card: every operational custom attribute (not tracking, not
// the Tipo/Estado pair shown as chips in the summary card), used ones
// first, with the same inline editing as the old sidebar. The loss-reason
// attribute is pinned on top (highlighted when the contact is lost and it
// is empty), exactly like the previous behaviour of the fork.
const props = defineProps({
  contact: { type: Object, required: true },
  // 'plain' rinde sólo las filas, para incrustarlo en la tarjeta CONTACTO
  // del panel de la conversación.
  variant: {
    type: String,
    default: 'card',
    validator: value => ['card', 'plain'].includes(value),
  },
});

const isPlain = computed(() => props.variant === 'plain');

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const contactAttributes = useMapGetter('attributes/getContactAttributes');

const LOSS_REASON_ATTRIBUTE_KEY = 'motivo_perdida';
const LOST_STAGE_VALUE = 'Perdido';

const customAttributes = computed(
  () => props.contact.customAttributes || props.contact.custom_attributes || {}
);

// Los atributos sincronizados con etiquetas ya se editan como chip en la
// tarjeta de contacto, así que no se repiten en esta lista.
const chipKeys = computed(() =>
  syncedListAttributes(contactAttributes.value).map(
    attribute => attribute.attributeKey
  )
);

const withValue = attribute => ({
  ...attribute,
  value: customAttributes.value[attribute.attributeKey] ?? '',
  isUsed: attribute.attributeKey in customAttributes.value,
});

const lossReasonAttribute = computed(() => {
  const attribute = (contactAttributes.value || []).find(
    a => a.attributeKey === LOSS_REASON_ATTRIBUTE_KEY
  );
  return attribute ? withValue(attribute) : null;
});

const stageAttribute = computed(() =>
  findListAttribute(
    contactAttributes.value,
    'estado_contacto',
    STAGE_ATTRIBUTE_HINT
  )
);
const isContactLost = computed(
  () =>
    !!stageAttribute.value &&
    customAttributes.value[stageAttribute.value.attributeKey] ===
      LOST_STAGE_VALUE
);
const hasEmptyLossReason = computed(
  () => isContactLost.value && !lossReasonAttribute.value?.value
);

const attributes = computed(() =>
  (contactAttributes.value || [])
    .filter(
      attribute =>
        !isTrackingAttribute(attribute) &&
        attribute.attributeKey !== LOSS_REASON_ATTRIBUTE_KEY &&
        !chipKeys.value.includes(attribute.attributeKey)
    )
    .map(withValue)
    .sort((a, b) => Number(b.isUsed) - Number(a.isUsed))
);

const hasAttributes = computed(
  () => attributes.value.length > 0 || lossReasonAttribute.value
);

const goToAttributeSettings = () => {
  router.push({
    name: 'attributes_list',
    params: { accountId: route.params.accountId },
  });
};
</script>

<template>
  <section
    class="flex flex-col gap-2"
    :class="
      isPlain
        ? ''
        : 'px-5 py-[18px] rounded-xl border border-n-weak bg-n-solid-2'
    "
  >
    <div v-if="!isPlain" class="flex items-center gap-2.5 mb-1">
      <h3
        class="m-0 text-[13.5px] font-semibold tracking-tight text-n-slate-12"
      >
        {{ t('CONTACTS_LAYOUT.DETAILS.ATTRIBUTES_CARD.TITLE') }}
      </h3>
      <button
        type="button"
        class="ml-auto text-xs font-semibold text-n-blue-text hover:underline"
        @click="goToAttributeSettings"
      >
        {{ t('CONTACTS_LAYOUT.DETAILS.ATTRIBUTES_CARD.ADD') }}
      </button>
    </div>

    <div
      v-if="isContactLost && lossReasonAttribute"
      class="flex flex-col gap-1 px-2 py-1 -mx-2 rounded-lg"
      :class="hasEmptyLossReason ? 'bg-n-ruby-3' : 'bg-n-alpha-1'"
    >
      <ContactCustomAttributeItem
        compact
        :contact-id="contact.id"
        :is-editing-view="lossReasonAttribute.isUsed"
        :attribute="lossReasonAttribute"
      />
      <p v-if="hasEmptyLossReason" class="m-0 text-xs text-n-ruby-11 px-0.5">
        {{ t('CONTACTS_LAYOUT.DETAILS.ATTRIBUTES_CARD.LOSS_REASON_HINT') }}
      </p>
    </div>

    <ContactCustomAttributeItem
      v-for="attribute in attributes"
      :key="attribute.id"
      compact
      :contact-id="contact.id"
      :is-editing-view="attribute.isUsed"
      :attribute="attribute"
    />

    <p v-if="!hasAttributes" class="m-0 text-xs text-n-slate-10">
      {{ t('CONTACTS_LAYOUT.DETAILS.ATTRIBUTES_CARD.EMPTY') }}
    </p>
  </section>
</template>
